import { X, TrendingUp, TrendingDown, Minus, AlertTriangle, Shield, FileText, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import {
  getStatus,
  getStatusLabel,
  getStatusColor,
  getOpportunityStatus,
  getOpportunityStatusLabel,
  getOpportunityStatusColor,
  getTrajectoryLabel,
  getTrajectoryArrow,
  getTrajectoryColor,
  getRegulatoryStatusLabel,
  getRegulatoryStatusColor,
  type Trajectory,
  type RegulatoryStatusType,
} from '@/data/regulatoryData';
import type { CountryScore } from '@/services/scoreService';
import TrendChart from './TrendChart';
import ArticleBrowser from './ArticleBrowser';
import { FlagIcon } from './FlagIcon';

interface ScoreExplainerProps {
  jurisdiction: CountryScore;
  onClose: () => void;
}

const componentConfig = [
  { key: 'enforcement', name: 'Enforcement', weight: 30, icon: Shield, description: 'Fines, penalties, and license actions' },
  { key: 'regulatory_change', name: 'Regulatory Change', weight: 25, icon: FileText, description: 'New laws and framework changes' },
  { key: 'rhetoric', name: 'Rhetoric', weight: 25, icon: AlertTriangle, description: 'Official statements and warnings' },
  { key: 'market_stability', name: 'Market Stability', weight: 20, icon: Activity, description: 'Operator exits and market shifts' },
] as const;

const statusDescriptions: Record<string, string> = {
  'Stable': 'Low regulatory pressure. Favorable operating environment.',
  'Watch': 'Increasing activity. Monitor for changes.',
  'Tightening': 'Active enforcement and regulatory pressure.',
  'High Pressure': 'Major regulatory action underway. High risk.',
};

export default function ScoreExplainer({ jurisdiction, onClose }: ScoreExplainerProps) {
  const status = getStatus(jurisdiction.score);
  const statusLabel = getStatusLabel(status);
  const statusColor = getStatusColor(status);

  // Opportunity score calculations
  const opportunityScore = jurisdiction.opportunity_score ?? (100 - jurisdiction.score);
  const oppStatus = getOpportunityStatus(opportunityScore);
  const oppStatusLabel = getOpportunityStatusLabel(oppStatus);
  const oppStatusColor = getOpportunityStatusColor(oppStatus);

  // Trajectory
  const trajectory = (jurisdiction.trajectory || 'stable') as Trajectory;
  const trajectoryLabel = getTrajectoryLabel(trajectory);
  const trajectoryArrow = getTrajectoryArrow(trajectory);
  const trajectoryColor = getTrajectoryColor(trajectory);

  // Get trend icon
  const TrendIcon = jurisdiction.trend === 'up' ? TrendingUp
    : jurisdiction.trend === 'down' ? TrendingDown
    : Minus;

  const trendColor = jurisdiction.trend === 'up' ? 'text-red-500'
    : jurisdiction.trend === 'down' ? 'text-green-500'
    : 'text-gray-500';

  // Delta formatting
  const deltaPrefix = jurisdiction.delta_30d > 0 ? '+' : '';
  const deltaValue = `${deltaPrefix}${jurisdiction.delta_30d}`;

  const components = jurisdiction.components || null;

  const coverageFactor = jurisdiction.coverage_factor ?? 0.8;

  return (
    <div className="fixed inset-0 z-[9999] flex items-stretch">
      {/* Backdrop */}
      <div
        className="flex-1 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="w-full max-w-md bg-card border-l border-border overflow-y-auto animate-slide-in-right">
        {/* Header */}
        <div className="sticky top-0 bg-card/95 backdrop-blur-sm border-b border-border p-4 z-10">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <FlagIcon code={jurisdiction.code} size="sm" className="shadow-md" />
              <div>
                <h2 className="text-xl font-bold text-text-primary">{jurisdiction.name}</h2>
                {jurisdiction.jurisdiction_type && jurisdiction.jurisdiction_type !== 'country' && (
                  <p className="text-sm text-text-secondary mt-0.5">
                    {jurisdiction.jurisdiction_type === 'us_state' ? 'United States' : 'Canada'}
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-text-secondary hover:text-text-primary"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="p-4 space-y-6">
          {/* Data confidence warning */}
          {(jurisdiction.stale || jurisdiction.low_confidence) && (
            <div className="flex items-center gap-2 text-sm text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded-lg px-3 py-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>
                {jurisdiction.stale
                  ? jurisdiction.stale_reason || 'Data may be outdated'
                  : 'Limited signal data — score has lower confidence'}
              </span>
            </div>
          )}

          {/* Trajectory Banner */}
          <div
            className="flex items-center justify-between px-3 py-2 rounded-lg"
            style={{ backgroundColor: `${trajectoryColor}15`, borderLeft: `3px solid ${trajectoryColor}` }}
          >
            <div className="flex items-center gap-2">
              <span className="text-lg" style={{ color: trajectoryColor }}>{trajectoryArrow}</span>
              <span className="text-sm font-medium" style={{ color: trajectoryColor }}>
                {trajectoryLabel}
              </span>
            </div>
            <span className="text-xs text-text-secondary">Regulatory Trajectory</span>
          </div>

          {/* Dual Score Overview */}
          <Card className="bg-card-secondary">
            <CardContent className="p-4">
              {/* Two-column score display */}
              <div className="grid grid-cols-2 gap-4">
                {/* Pressure Score */}
                <div className="space-y-2">
                  <div className="text-xs text-text-secondary uppercase tracking-wide">
                    Pressure
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-text-primary">
                      {jurisdiction.score.toFixed(0)}
                    </span>
                    <div className="flex items-center gap-1">
                      <TrendIcon className={`h-3 w-3 ${trendColor}`} />
                      <span className={`text-xs ${trendColor}`}>
                        {deltaValue}
                      </span>
                    </div>
                  </div>
                  <Badge
                    className="text-xs px-2 py-0.5"
                    style={{
                      backgroundColor: `${statusColor}20`,
                      color: statusColor,
                      borderColor: statusColor,
                    }}
                  >
                    {statusLabel}
                  </Badge>
                  {/* Progress bar */}
                  <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${jurisdiction.score}%`,
                        backgroundColor: statusColor,
                      }}
                    />
                  </div>
                </div>

                {/* Opportunity Score */}
                <div className="space-y-2 border-l border-border pl-4">
                  <div className="text-xs text-text-secondary uppercase tracking-wide">
                    Opportunity
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-text-primary">
                      {opportunityScore.toFixed(0)}
                    </span>
                  </div>
                  <Badge
                    className="text-xs px-2 py-0.5"
                    style={{
                      backgroundColor: `${oppStatusColor}20`,
                      color: oppStatusColor,
                      borderColor: oppStatusColor,
                    }}
                  >
                    {oppStatusLabel}
                  </Badge>
                  {/* Progress bar */}
                  <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${opportunityScore}%`,
                        backgroundColor: oppStatusColor,
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <p className="text-xs text-text-tertiary mt-4 pt-3 border-t border-border">
                {statusDescriptions[jurisdiction.status]}
              </p>
            </CardContent>
          </Card>

          {/* Regulatory Status Grid */}
          {jurisdiction.regulatory_status && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-text-primary">
                  Regulatory Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'online_casino', label: 'Online Casino' },
                    { key: 'online_sports', label: 'Online Sports' },
                    { key: 'land_casino', label: 'Land Casino' },
                    { key: 'land_sports', label: 'Land Sports' },
                  ].map((item) => {
                    const regStatus = jurisdiction.regulatory_status?.[item.key as keyof typeof jurisdiction.regulatory_status] as RegulatoryStatusType | undefined;
                    if (!regStatus) return null;
                    const regStatusLabel = getRegulatoryStatusLabel(regStatus);
                    const regStatusColor = getRegulatoryStatusColor(regStatus);
                    return (
                      <div
                        key={item.key}
                        className="flex items-center justify-between p-2 rounded-md bg-black/20"
                      >
                        <span className="text-xs text-text-secondary">{item.label}</span>
                        <div className="flex items-center gap-1.5">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: regStatusColor }}
                          />
                          <span className="text-xs font-medium" style={{ color: regStatusColor }}>
                            {regStatusLabel}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {jurisdiction.market_maturity !== undefined && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-text-secondary">Market Maturity</span>
                      <span className="text-text-primary font-medium">
                        {(jurisdiction.market_maturity * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Component Breakdown */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-text-primary">
                Score Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!components && (
                <p className="text-sm text-text-tertiary">Component breakdown not available</p>
              )}
              {components && componentConfig.map((comp) => {
                const score = components[comp.key as keyof typeof components] || 50;
                const Icon = comp.icon;

                return (
                  <div key={comp.key}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-text-secondary" />
                        <span className="text-text-primary">{comp.name}</span>
                        <span className="text-text-tertiary">({comp.weight}%)</span>
                      </div>
                      <span className="font-medium text-text-primary">{score}</span>
                    </div>
                    <Progress
                      value={score}
                      className="h-2"
                      style={{
                        // @ts-expect-error CSS custom property
                        '--progress-background': getStatusColor(getStatus(score)),
                      }}
                    />
                    <p className="text-xs text-text-tertiary mt-1">{comp.description}</p>
                  </div>
                );
              })}

              {/* Coverage Factor */}
              <div className="pt-2 border-t border-border">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary">Signal Coverage</span>
                  <span className="text-text-primary font-medium">
                    {(coverageFactor * 100).toFixed(0)}%
                  </span>
                </div>
                <p className="text-xs text-text-tertiary mt-1">
                  {coverageFactor >= 0.8 ? 'High confidence — rich signal data' :
                   coverageFactor >= 0.5 ? 'Medium confidence — moderate signal data' :
                   'Low confidence — limited signal data'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Trend Chart */}
          <TrendChart
            jurisdictionCode={jurisdiction.code}
            jurisdictionName={jurisdiction.name}
          />

          {/* Key Drivers */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-text-primary">
                Key Drivers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {jurisdiction.drivers.map((driver, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <span className="text-text-tertiary mt-0.5 font-medium">{index + 1}.</span>
                    <span className="text-text-primary">{driver}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Latest Signal */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-text-primary">
                Latest Signal
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-text-primary">
                {jurisdiction.latest_signal.text}
              </p>
              <div className="flex items-center justify-between mt-3 text-xs text-text-tertiary">
                <span>
                  {jurisdiction.latest_signal.days_ago} {jurisdiction.latest_signal.days_ago === 1 ? 'day' : 'days'} ago
                </span>
                {jurisdiction.latest_signal.severity && (
                  <span>
                    Severity: {(jurisdiction.latest_signal.severity * 100).toFixed(0)}%
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Article Browser */}
          <ArticleBrowser
            jurisdictionCode={jurisdiction.code}
            jurisdictionName={jurisdiction.name}
          />

          {/* Methodology Note */}
          <p className="text-xs text-text-tertiary text-center px-4">
            Scores are calculated from regulatory signals collected via GDELT, RSS feeds, and official sources.
            Updated daily.
          </p>
        </div>
      </div>
    </div>
  );
}
